/**
 * LandingPagesPanel — list, generate, edit, reorder and publish landing pages.
 *
 * Backend surface used:
 *   GET    /api/landing                 – list pages (workspace-scoped)
 *   POST   /api/landing/generate        – AI-generate a new sectioned page
 *   GET    /api/landing/{id}            – fetch one
 *   PATCH  /api/landing/{id}            – save sections/title/slug/status
 *   DELETE /api/landing/{id}            – soft delete
 *
 * Section model: each section is { kind, ...content }. We render generic
 * editors for known kinds (hero, value, cta, faq, pricing, transformation,
 * testimonials) and a free-form raw editor for anything else so creators
 * can tweak AI output without losing data.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  LayoutTemplate, Sparkles, Plus, Loader2, Save, Trash2, GripVertical,
  Globe, EyeOff, Eye, Copy, X, RefreshCw, ExternalLink, Share2,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const KIND_LABEL = {
  hero: "Hero", value: "Value props", transformation: "Transformation",
  pricing: "Pricing", faq: "FAQ", testimonials: "Testimonials", cta: "Call to action",
};

const KIND_DEFAULTS = {
  hero: { kind: "hero", headline: "New hero headline", subheadline: "Supporting line", cta: "Get started" },
  value: { kind: "value", title: "Why it works", bullets: ["Outcome 1", "Outcome 2", "Outcome 3"] },
  transformation: { kind: "transformation", before: "Where they are now", after: "Where they want to be" },
  pricing: { kind: "pricing", label: "Plan", price: 49, format: "one_time", cta: "Buy now" },
  faq: { kind: "faq", items: [{ q: "Question?", a: "Answer." }] },
  testimonials: { kind: "testimonials", items: [{ name: "Name", quote: "Quote here" }] },
  cta: { kind: "cta", text: "Ready to start?", button: "Start now" },
};

const PALETTE = Object.keys(KIND_DEFAULTS);

// ----------------- Generic editors per section kind ------------------------

function TextField({ label, value, onChange, multiline, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-meta font-bold block mb-1">{label}</span>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          data-testid={testid}
          className="w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
      )}
    </label>
  );
}

function ListEditor({ label, items, onChange, fields, testid }) {
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...items, fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {})]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-meta font-bold block mb-1">{label}</span>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-line bg-white p-2 space-y-1.5" data-testid={`${testid}-row-${i}`}>
            {fields.map((f) => (
              <input
                key={f.key}
                value={it[f.key] || ""}
                onChange={(e) => update(i, f.key, e.target.value)}
                placeholder={f.placeholder || f.key}
                className="w-full rounded-md border border-line bg-white px-2 py-1 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            ))}
            <button onClick={() => remove(i)} className="text-[10px] text-meta hover:text-danger inline-flex items-center gap-1">
              <X className="h-3 w-3" /> remove
            </button>
          </div>
        ))}
        <button onClick={add} className="text-xs text-ai font-semibold inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Add row
        </button>
      </div>
    </div>
  );
}

function BulletsEditor({ bullets, onChange, testid }) {
  const update = (i, val) => {
    const next = [...bullets];
    next[i] = val;
    onChange(next);
  };
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wider text-meta font-bold block mb-1">Bullets</span>
      <div className="space-y-1.5">
        {bullets.map((b, i) => (
          <input
            key={i}
            value={b}
            onChange={(e) => update(i, e.target.value)}
            className="w-full rounded-md border border-line bg-white px-2 py-1 text-xs text-ink"
            data-testid={`${testid}-bullet-${i}`}
          />
        ))}
        <button onClick={() => onChange([...bullets, "New bullet"])} className="text-xs text-ai font-semibold inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Bullet
        </button>
      </div>
    </div>
  );
}

function SectionEditor({ section, onChange, idx }) {
  const k = section.kind;
  const set = (key, val) => onChange({ ...section, [key]: val });

  if (k === "hero") {
    return (
      <div className="space-y-2">
        <TextField label="Headline" value={section.headline} onChange={(v) => set("headline", v)} testid={`sec-${idx}-headline`} />
        <TextField label="Subheadline" value={section.subheadline} multiline onChange={(v) => set("subheadline", v)} testid={`sec-${idx}-subheadline`} />
        <TextField label="CTA" value={section.cta} onChange={(v) => set("cta", v)} testid={`sec-${idx}-cta`} />
      </div>
    );
  }
  if (k === "value") {
    return (
      <div className="space-y-2">
        <TextField label="Title" value={section.title} onChange={(v) => set("title", v)} testid={`sec-${idx}-title`} />
        <BulletsEditor bullets={section.bullets || []} onChange={(v) => set("bullets", v)} testid={`sec-${idx}`} />
      </div>
    );
  }
  if (k === "transformation") {
    return (
      <div className="space-y-2">
        <TextField label="Before" value={section.before} multiline onChange={(v) => set("before", v)} testid={`sec-${idx}-before`} />
        <TextField label="After" value={section.after} multiline onChange={(v) => set("after", v)} testid={`sec-${idx}-after`} />
      </div>
    );
  }
  if (k === "pricing") {
    return (
      <div className="space-y-2">
        <TextField label="Label" value={section.label} onChange={(v) => set("label", v)} testid={`sec-${idx}-label`} />
        <div className="grid grid-cols-2 gap-2">
          <TextField label="Price" value={section.price} onChange={(v) => set("price", Number(v) || 0)} testid={`sec-${idx}-price`} />
          <TextField label="Format" value={section.format} onChange={(v) => set("format", v)} testid={`sec-${idx}-format`} />
        </div>
        <TextField label="CTA" value={section.cta} onChange={(v) => set("cta", v)} testid={`sec-${idx}-pcta`} />
      </div>
    );
  }
  if (k === "faq") {
    return (
      <ListEditor label="FAQ items" items={section.items || []} onChange={(v) => set("items", v)}
        fields={[{ key: "q", placeholder: "Question" }, { key: "a", placeholder: "Answer" }]}
        testid={`sec-${idx}-faq`} />
    );
  }
  if (k === "testimonials") {
    return (
      <ListEditor label="Testimonials" items={section.items || []} onChange={(v) => set("items", v)}
        fields={[{ key: "name", placeholder: "Name" }, { key: "quote", placeholder: "Quote" }]}
        testid={`sec-${idx}-test`} />
    );
  }
  if (k === "cta") {
    return (
      <div className="space-y-2">
        <TextField label="Text" value={section.text} multiline onChange={(v) => set("text", v)} testid={`sec-${idx}-text`} />
        <TextField label="Button" value={section.button} onChange={(v) => set("button", v)} testid={`sec-${idx}-button`} />
      </div>
    );
  }
  return (
    <TextField
      label={`Raw ${k} JSON`}
      multiline
      value={JSON.stringify(section, null, 2)}
      onChange={(v) => {
        try { onChange(JSON.parse(v)); } catch { /* invalid mid-edit */ }
      }}
      testid={`sec-${idx}-raw`}
    />
  );
}

// ----------------- Main panel ---------------------------------------------

export default function LandingPagesPanel() {
  const [pages, setPages] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ title: "", selling: "", audience: "", price: "", tone: "Confident" });
  const [generating, setGenerating] = useState(false);
  const [drag, setDrag] = useState({ from: null, over: null });

  const load = async () => {
    try {
      const r = await api.get("/landing");
      setPages(r.data || []);
      if (!activeId && (r.data || []).length) {
        setActiveId(r.data[0].id);
      }
    } catch { setPages([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Fetch active page details when selection changes
  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    api.get(`/landing/${activeId}`).then(r => setActive(r.data)).catch(() => setActive(null));
  }, [activeId]);

  const dirty = useMemo(() => {
    if (!active || !pages) return false;
    const orig = pages.find((p) => p.id === active.id);
    if (!orig) return false;
    return JSON.stringify(orig.sections || []) !== JSON.stringify(active.sections || []) ||
           orig.title !== active.title || orig.slug !== active.slug;
  }, [active, pages]);

  const setSections = (sections) => setActive((p) => ({ ...p, sections }));

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await api.patch(`/landing/${active.id}`, {
        title: active.title, slug: active.slug, sections: active.sections,
      });
      setActive(r.data);
      await load();
      toast.success("Landing page saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const togglePublish = async () => {
    if (!active) return;
    const next = active.status === "published" ? "draft" : "published";
    try {
      const r = await api.patch(`/landing/${active.id}`, { status: next });
      setActive(r.data);
      await load();
      toast.success(next === "published" ? "Published" : "Unpublished");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    }
  };

  const del = async () => {
    if (!active || !window.confirm("Delete this landing page?")) return;
    try {
      await api.delete(`/landing/${active.id}`);
      setActiveId(null);
      setActive(null);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const generate = async () => {
    const f = genForm;
    if (!f.title || !f.selling || !f.audience) {
      toast.error("Title, selling and audience required");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/landing/generate", {
        title: f.title, selling: f.selling, audience: f.audience,
        price: f.price ? Number(f.price) : undefined, tone: f.tone,
      });
      toast.success("AI page generated");
      setGenOpen(false);
      setGenForm({ title: "", selling: "", audience: "", price: "", tone: "Confident" });
      await load();
      setActiveId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    }
    setGenerating(false);
  };

  // Section reordering via native HTML5 drag
  const onSecDragStart = (i) => () => setDrag({ from: i, over: null });
  const onSecDragOver = (i) => (e) => { e.preventDefault(); setDrag((d) => ({ ...d, over: i })); };
  const onSecDrop = (i) => (e) => {
    e.preventDefault();
    const { from } = drag;
    if (from === null || from === i) { setDrag({ from: null, over: null }); return; }
    const next = [...(active.sections || [])];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setSections(next);
    setDrag({ from: null, over: null });
  };

  const addSection = (kind) => {
    const tpl = KIND_DEFAULTS[kind] || { kind };
    setSections([...(active.sections || []), JSON.parse(JSON.stringify(tpl))]);
  };
  const removeSection = (i) => setSections((active.sections || []).filter((_, idx) => idx !== i));

  if (pages === null) {
    return <div className="p-10 text-meta inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading landing pages…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 sm:p-6 lg:p-8" data-testid="landing-pages-panel">
      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-4">

        {/* Left rail — list */}
        <aside className="space-y-3" data-testid="landing-list">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold inline-flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4 text-ai" /> Landing pages
            </h2>
            <button
              onClick={() => setGenOpen(true)}
              data-testid="landing-generate-btn"
              className="rounded-full ashra-gradient-bg px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md"
            >
              <Sparkles className="h-3 w-3" /> Generate
            </button>
          </div>
          {pages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white p-5 text-center text-xs text-meta" data-testid="landing-empty">
              No pages yet. Generate one with AI to begin.
            </div>
          ) : (
            <div className="space-y-1.5">
              {pages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActiveId(p.id)}
                  data-testid={`landing-row-${p.id}`}
                  className={`w-full text-left rounded-xl border ${activeId === p.id ? "border-ai bg-ai-soft" : "border-line bg-white"} px-3 py-2 hover:border-ai transition`}
                >
                  <p className="text-sm font-semibold text-ink truncate">{p.title}</p>
                  <p className="text-[11px] text-meta truncate">/{p.slug} · {p.status}</p>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Right pane — editor */}
        <main data-testid="landing-editor">
          {!active ? (
            <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
              <LayoutTemplate className="h-8 w-8 text-zinc-300 mx-auto" />
              <p className="mt-2 text-sm font-medium text-zinc-700">Pick a page from the left, or generate one with AI.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Page meta */}
              <div className="surface p-5 space-y-3" data-testid="landing-meta">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <input
                      value={active.title || ""}
                      onChange={(e) => setActive({ ...active, title: e.target.value })}
                      className="w-full font-display text-xl font-bold text-ink bg-transparent border-0 outline-none focus:ring-0"
                      data-testid="landing-title-input"
                    />
                    <div className="flex items-center gap-2 mt-1 text-xs text-meta">
                      <span>/</span>
                      <input
                        value={active.slug || ""}
                        onChange={(e) => setActive({ ...active, slug: e.target.value })}
                        className="rounded-md bg-zinc-50 px-2 py-1 text-xs text-ink-soft border border-line focus:outline-none focus:ring-1 focus:ring-violet-500 font-mono"
                        data-testid="landing-slug-input"
                      />
                      <span className={`pill ${active.status === "published" ? "pill-success" : "pill-neutral"}`}>{active.status}</span>
                    </div>
                    {/* Published-state action row */}
                    {active.status === "published" && active.public_url && (
                      <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="landing-published-actions">
                        <a
                          href={active.public_url} target="_blank" rel="noopener noreferrer"
                          data-testid="landing-open-btn"
                          className="rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 hover:bg-ai/15"
                        >
                          <ExternalLink className="h-3 w-3" /> Open
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(active.public_url);
                            toast.success("Public URL copied");
                          }}
                          data-testid="landing-copy-link-btn"
                          className="rounded-full bg-zinc-100 text-ink-soft px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 hover:bg-zinc-200"
                        >
                          <Copy className="h-3 w-3" /> Copy link
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (navigator.share) {
                              try {
                                await navigator.share({ title: active.title, url: active.public_url });
                              } catch { /* user cancelled */ }
                            } else {
                              navigator.clipboard?.writeText(active.public_url);
                              toast.success("Link copied — paste anywhere to share");
                            }
                          }}
                          data-testid="landing-share-btn"
                          className="rounded-full bg-zinc-100 text-ink-soft px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 hover:bg-zinc-200"
                        >
                          <Share2 className="h-3 w-3" /> Share
                        </button>
                        <span className="text-[11px] text-meta font-mono ml-1 truncate max-w-[280px]" data-testid="landing-public-url">
                          {active.public_url}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={togglePublish}
                      data-testid="landing-publish-btn"
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${active.status === "published" ? "bg-zinc-100 text-ink-soft hover:bg-zinc-200" : "ashra-gradient-bg text-white"}`}
                    >
                      {active.status === "published" ? <><EyeOff className="h-3 w-3" /> Unpublish</> : <><Globe className="h-3 w-3" /> Publish</>}
                    </button>
                    <button
                      onClick={save}
                      disabled={busy || !dirty}
                      data-testid="landing-save-btn"
                      className="rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                    </button>
                    <button onClick={del} className="rounded-full bg-zinc-100 hover:bg-rose-100 hover:text-danger text-meta p-1.5" data-testid="landing-delete-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Sections */}
              <div className="space-y-2" data-testid="landing-sections">
                {(active.sections || []).map((s, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={onSecDragStart(i)}
                    onDragOver={onSecDragOver(i)}
                    onDrop={onSecDrop(i)}
                    onDragEnd={() => setDrag({ from: null, over: null })}
                    className={`surface p-4 ${drag.over === i && drag.from !== null && drag.from !== i ? "ring-2 ring-ai" : ""} ${drag.from === i ? "opacity-50" : ""}`}
                    data-testid={`landing-section-${i}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="cursor-grab active:cursor-grabbing text-meta" data-testid={`landing-section-drag-${i}`}>
                          <GripVertical className="h-4 w-4" />
                        </span>
                        <span className="pill pill-neutral text-[10px]">{KIND_LABEL[s.kind] || s.kind}</span>
                        <span className="text-[10px] text-meta">step {i + 1}</span>
                      </div>
                      <button onClick={() => removeSection(i)} className="text-meta hover:text-danger" data-testid={`landing-section-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <SectionEditor section={s} idx={i} onChange={(next) => {
                      const ns = [...(active.sections || [])];
                      ns[i] = next;
                      setSections(ns);
                    }} />
                  </div>
                ))}

                {/* Add section palette */}
                <div className="rounded-2xl border border-dashed border-line bg-white p-4" data-testid="landing-section-palette">
                  <p className="text-[10px] uppercase tracking-wider text-meta font-bold mb-2">Add section</p>
                  <div className="flex flex-wrap gap-1.5">
                    {PALETTE.map((k) => (
                      <button
                        key={k}
                        onClick={() => addSection(k)}
                        className="rounded-full bg-white border border-line px-2.5 py-1 text-[11px] font-medium text-ink-soft hover:border-ai hover:text-ai transition inline-flex items-center gap-1"
                        data-testid={`landing-add-section-${k}`}
                      >
                        <Plus className="h-3 w-3" /> {KIND_LABEL[k] || k}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Generate modal */}
      {genOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-50" onClick={() => !generating && setGenOpen(false)} data-testid="landing-generate-modal">
          <div className="bg-white rounded-3xl border border-line shadow-xl p-6 w-[min(520px,92vw)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-ai" /> Generate landing page</h3>
              <button onClick={() => setGenOpen(false)} disabled={generating} className="text-meta hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <TextField label="Title" value={genForm.title} onChange={(v) => setGenForm({ ...genForm, title: v })} placeholder="The Creator Growth Playbook" testid="gen-title" />
              <TextField label="What are you selling?" value={genForm.selling} multiline onChange={(v) => setGenForm({ ...genForm, selling: v })} placeholder="e.g. 6-week cohort coaching program" testid="gen-selling" />
              <TextField label="Who is it for?" value={genForm.audience} multiline onChange={(v) => setGenForm({ ...genForm, audience: v })} placeholder="e.g. solo creators making $5–25K/mo" testid="gen-audience" />
              <div className="grid grid-cols-2 gap-2">
                <TextField label="Price (USD)" value={genForm.price} onChange={(v) => setGenForm({ ...genForm, price: v })} placeholder="49" testid="gen-price" />
                <TextField label="Tone" value={genForm.tone} onChange={(v) => setGenForm({ ...genForm, tone: v })} placeholder="Confident · warm" testid="gen-tone" />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setGenOpen(false)} disabled={generating} className="rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-ink-soft">Cancel</button>
              <button onClick={generate} disabled={generating} data-testid="gen-confirm" className="rounded-full ashra-gradient-bg px-4 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50">
                {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {generating ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
